import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { HintLink } from '../../_components/HintLink';
import { CodeInput, NotificationList } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { ChallengeMFAResponse, ChallengeName, UserRepository } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Hint, Paragraph, Text } from '~/styles';
import { log } from '~/util';

interface MultifactorAuthFormProps {
  challengeName: ChallengeName;
  phoneNumber?: string;
  onCancel: () => void;
  onSuccess: (response: ChallengeMFAResponse) => void;
}

interface MultifactorAuthFormValues {
  code: string;
}

const Resend = styled.span`
  display: inline-block;
  cursor: pointer;
  color: ${({ theme }) => theme.primary.background};
`;

export const MultifactorAuthForm: React.FC<MultifactorAuthFormProps> = ({
  challengeName,
  phoneNumber,
  onCancel,
  onSuccess: onSuccessCallback,
}) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const { control, handleSubmit, watch, setValue } = useForm<MultifactorAuthFormValues>({
    defaultValues: {
      code: '',
    },
  });

  const onSuccess = useCallback(
    ({ code: codeValue }: MultifactorAuthFormValues) => {
      if (loading) {
        return;
      }

      setLoading(true);

      UserRepository.challengeMfa({ code: codeValue })
        .then((response) => {
          onSuccessCallback(response);
        })
        .catch((error) => {
          setLoading(false);
          setValue('code', '');

          addDangerNotification({
            content: error.message,
            display: 'page-login',
          });
        });
    },
    [loading, addDangerNotification, onSuccessCallback]
  );

  const handleResend = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      UserRepository.resendMfa()
        .then(() => {
          log({
            name: 'User MFA resend',
            context: {
              phoneNumber,
            },
          });

          addSuccessNotification({
            content: phoneNumber ? `Code sent to ${phoneNumber}` : 'Code sent',
            display: 'page-login',
          });
        })
        .catch((error) => {
          if (error.code === 'invalid_session') {
            onCancel();
          }

          addDangerNotification({
            content: error.message,
            display: 'page-login',
          });
        });
    },
    [phoneNumber, navigate, addSuccessNotification, addDangerNotification]
  );

  const code = watch('code');
  useEffect(() => {
    if (code && code.length === 6) {
      onSuccess({ code });
    }
  }, [code]);

  const description = useMemo(() => {
    switch (challengeName) {
      case ChallengeName.CHALLENGE_MFA_SMS:
        return `Please enter the 6-digit code we have sent to ${phoneNumber}.`;
      case ChallengeName.CHALLENGE_MFA_TOTP:
        return 'Please enter the 6-digit code from your authenticator app.';
      default:
        return undefined;
    }
  }, [challengeName, phoneNumber]);

  return (
    <>
      <Flex flexDirection="column" gap={8}>
        <Text size={16} weight="semibold">
          Authenticate
        </Text>
        <Paragraph>{description}</Paragraph>
      </Flex>
      <form onSubmit={handleSubmit(onSuccess)}>
        <NotificationList display="page-login" />

        <Flex flexDirection="column" gap={24}>
          <Controller<MultifactorAuthFormValues, 'code'>
            name="code"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              minLength: 6,
              maxLength: 6,
            }}
            render={({ field: { onChange, value } }) => (
              <CodeInput
                placeholder="######"
                autoFocus
                type="number"
                value={value}
                onChange={onChange}
                isLoading={loading}
              />
            )}
          />
          {challengeName === ChallengeName.CHALLENGE_MFA_SMS && (
            <Hint>
              Haven't received the code? <Resend onClick={handleResend}>Resend</Resend>
            </Hint>
          )}
          <HintLink onClick={onCancel}>Back to Login</HintLink>
        </Flex>
      </form>
    </>
  );
};
